import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Save, X, Power, Play } from 'lucide-react';
import {
  useRule,
  useCreateRule,
  useUpdateRule,
  useToggleRuleActive,
  useTestRule,
  useEventTypes,
} from '../../../hooks/useRules';
import { useTemplates } from '../../../hooks/useTemplates';
import { formatRuleTiming } from '../../../utils/formatters';
import { useToast } from '../../../contexts/ToastContext';
import ConditionBuilder from './ConditionBuilder';

const TIMEZONES = [
  { value: 'guest', label: 'Guest timezone' },
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'UTC', label: 'UTC' },
];

function delayToFormValues(delayMinutes) {
  if (delayMinutes === 0) return { direction: 'immediate', amount: 0, unit: 'minutes' };

  const abs = Math.abs(delayMinutes);
  const direction = delayMinutes < 0 ? 'before' : 'after';

  if (abs >= 1440 && abs % 1440 === 0) {
    return { direction, amount: abs / 1440, unit: 'days' };
  }
  if (abs >= 60 && abs % 60 === 0) {
    return { direction, amount: abs / 60, unit: 'hours' };
  }
  return { direction, amount: abs, unit: 'minutes' };
}

function formValuesToDelay(direction, amount, unit) {
  if (direction === 'immediate') return 0;
  const multipliers = { minutes: 1, hours: 60, days: 1440 };
  const minutes = amount * (multipliers[unit] || 1);
  return direction === 'before' ? -minutes : minutes;
}

function EmailRuleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const toast = useToast();

  const { rule, isLoading: loadingRule } = useRule(id);
  const createMutation = useCreateRule();
  const updateMutation = useUpdateRule();
  const toggleMutation = useToggleRuleActive();
  const testMutation = useTestRule();
  const { eventTypes } = useEventTypes();
  const { templates: activeTemplates } = useTemplates({ isActive: 'true' });

  const [sendWindowEnabled, setSendWindowEnabled] = useState(false);
  const [delayDirection, setDelayDirection] = useState('immediate');
  const [delayAmount, setDelayAmount] = useState(0);
  const [delayUnit, setDelayUnit] = useState('hours');
  const [testResult, setTestResult] = useState(null);
  const [conditionExpr, setConditionExpr] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue, // eslint-disable-line no-unused-vars
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      event_type: 'reservation_approved',
      template_id: '',
      send_window_start: '08:00',
      send_window_end: '20:00',
      send_window_tz: 'guest',
      dedupe_key_tpl: '',
    },
  });

  const watchedEventType = watch('event_type');

  // Load rule data into form
  useEffect(() => {
    if (rule) {
      const delay = delayToFormValues(rule.delay_minutes || 0);
      setDelayDirection(delay.direction);
      setDelayAmount(delay.amount);
      setDelayUnit(delay.unit);

      const hasWindow = !!(rule.send_window_start && rule.send_window_end);
      setSendWindowEnabled(hasWindow);
      setConditionExpr(rule.condition_expr || null);

      reset({
        name: rule.name,
        event_type: rule.event_type,
        template_id: rule.template_id,
        send_window_start: rule.send_window_start || '08:00',
        send_window_end: rule.send_window_end || '20:00',
        send_window_tz: rule.send_window_tz || 'guest',
        dedupe_key_tpl: rule.dedupe_key_tpl || '',
      });
    }
  }, [rule, reset]);

  const computedDelayMinutes = useMemo(() => {
    return formValuesToDelay(delayDirection, delayAmount, delayUnit);
  }, [delayDirection, delayAmount, delayUnit]);

  const timingPreview = useMemo(() => {
    const timing = formatRuleTiming(computedDelayMinutes, watchedEventType);
    if (!sendWindowEnabled) return `This email will be sent ${timing.toLowerCase()}.`;
    return `This email will be sent ${timing.toLowerCase()}, clamped to the send window.`;
  }, [computedDelayMinutes, watchedEventType, sendWindowEnabled]);

  const onSubmit = async (data) => {
    try {
      const payload = {
        name: data.name,
        event_type: data.event_type,
        template_id: data.template_id,
        delay_minutes: computedDelayMinutes,
        send_window_start: sendWindowEnabled ? data.send_window_start : null,
        send_window_end: sendWindowEnabled ? data.send_window_end : null,
        send_window_tz: sendWindowEnabled ? data.send_window_tz : null,
        dedupe_key_tpl: data.dedupe_key_tpl || null,
        condition_expr: conditionExpr,
      };

      if (isNew) {
        const result = await createMutation.mutateAsync(payload);
        toast.success('Rule created successfully');
        navigate(`/admin/email/rules/${result.data.id}`, { replace: true });
      } else {
        await updateMutation.mutateAsync({ id, ...payload });
        toast.success('Rule saved');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save rule');
    }
  };

  const handleToggleActive = async () => {
    if (!id) return;
    try {
      const result = await toggleMutation.mutateAsync(id);
      toast.success(`Rule ${result.data.is_active ? 'activated' : 'deactivated'}`);
    } catch (err) {
      toast.error('Failed to toggle rule status');
    }
  };

  const handleTestRule = async () => {
    if (!id) return;
    setTestResult(null);
    try {
      // Use sample data for testing
      const sampleContext = {
        reservation: {
          id: 42,
          reservationNumber: 'RES-2026-0042',
          checkIn: '2026-07-04',
          checkOut: '2026-07-07',
          nights: 3,
          status: 'approved',
          type: 'family',
        },
        customer: {
          firstName: 'Sarah',
          lastName: 'Chen',
          email: 'sarah.chen@example.com',
          family: 'Chen',
        },
        payment: {
          amount: 750.00,
          status: 'paid',
          refundable: true,
        },
      };
      const result = await testMutation.mutateAsync({ id, context: sampleContext });
      setTestResult(result.data);
    } catch (err) {
      console.error('Test failed:', err);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const saveError = createMutation.error || updateMutation.error;

  if (!isNew && loadingRule) {
    return (
      <div className="email-page">
        <div className="email-table-skeleton">
          <div className="skeleton-row" />
          <div className="skeleton-row" />
        </div>
      </div>
    );
  }

  return (
    <div className="email-page">
      <div className="email-page-header">
        <h3>{isNew ? 'New Rule' : 'Edit Rule'}</h3>
        <div className="email-editor-actions-top">
          {!isNew && rule && (
            <>
              <span className="email-editor-meta">
                {rule.is_active ? 'Active' : 'Inactive'}
              </span>
              <button
                type="button"
                className={`btn btn-sm ${rule.is_active ? 'btn-warning' : 'btn-success'}`}
                onClick={handleToggleActive}
                disabled={toggleMutation.isPending}
              >
                <Power size={14} />
                {rule.is_active ? 'Deactivate' : 'Activate'}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={handleTestRule}
                disabled={testMutation.isPending}
              >
                <Play size={14} />
                {testMutation.isPending ? 'Testing...' : 'Test Rule'}
              </button>
            </>
          )}
        </div>
      </div>

      {saveError && (
        <div className="email-error">
          {saveError.response?.data?.error || 'Failed to save rule.'}
        </div>
      )}

      {testResult && (
        <div className={`email-test-result ${testResult.conditionPassed ? 'pass' : 'fail'}`}>
          <strong>Test Result:</strong>
          <ul>
            <li>Condition: {testResult.conditionPassed ? 'Passed' : 'Failed'}</li>
            <li>Would send to: {testResult.wouldSendTo}</li>
            <li>Computed sendAt: {testResult.computedSendAt
              ? new Date(testResult.computedSendAt).toLocaleString()
              : 'N/A'}</li>
            {testResult.dedupeKey && <li>Dedupe key: <code>{testResult.dedupeKey}</code></li>}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Section 1: Basics */}
        <div className="email-form-section">
          <h4 className="email-section-title">Basics</h4>
          <div className="email-form-group">
            <label>Name</label>
            <input
              type="text"
              className={`email-form-input ${errors.name ? 'error' : ''}`}
              placeholder="e.g. Confirmation email for approved reservations"
              {...register('name', { required: 'Name is required' })}
            />
            {errors.name && <span className="email-form-error">{errors.name.message}</span>}
          </div>

          <div className="email-form-row">
            <div className="email-form-group">
              <label>Event Type</label>
              <select
                className="email-form-input"
                {...register('event_type', { required: 'Event type is required' })}
              >
                {eventTypes.map((et) => (
                  <option key={et.value} value={et.value}>{et.label}</option>
                ))}
              </select>
            </div>
            <div className="email-form-group">
              <label>Template</label>
              <select
                className={`email-form-input ${errors.template_id ? 'error' : ''}`}
                {...register('template_id', { required: 'Template is required' })}
              >
                <option value="">Select a template...</option>
                {activeTemplates.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>
                    {tpl.name} ({tpl.category})
                  </option>
                ))}
              </select>
              {errors.template_id && <span className="email-form-error">{errors.template_id.message}</span>}
            </div>
          </div>
        </div>

        {/* Section 2: Timing */}
        <div className="email-form-section">
          <h4 className="email-section-title">Timing</h4>

          <div className="email-form-row-inline">
            <div className="email-form-group">
              <label>Direction</label>
              <select
                className="email-form-input"
                value={delayDirection}
                onChange={(e) => {
                  setDelayDirection(e.target.value);
                  if (e.target.value === 'immediate') setDelayAmount(0);
                }}
              >
                <option value="immediate">Immediate</option>
                <option value="before">Before event</option>
                <option value="after">After event</option>
              </select>
            </div>

            {delayDirection !== 'immediate' && (
              <>
                <div className="email-form-group">
                  <label>Amount</label>
                  <input
                    type="number"
                    className="email-form-input"
                    min="0"
                    value={delayAmount}
                    onChange={(e) => setDelayAmount(parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="email-form-group">
                  <label>Unit</label>
                  <select
                    className="email-form-input"
                    value={delayUnit}
                    onChange={(e) => setDelayUnit(e.target.value)}
                  >
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                  </select>
                </div>
              </>
            )}
          </div>

          <div className="email-form-group">
            <label className="email-checkbox-label">
              <input
                type="checkbox"
                checked={sendWindowEnabled}
                onChange={(e) => setSendWindowEnabled(e.target.checked)}
              />
              Enable send window
            </label>
          </div>

          {sendWindowEnabled && (
            <div className="email-form-row-inline">
              <div className="email-form-group">
                <label>Start</label>
                <input
                  type="time"
                  className="email-form-input"
                  {...register('send_window_start')}
                />
              </div>
              <div className="email-form-group">
                <label>End</label>
                <input
                  type="time"
                  className="email-form-input"
                  {...register('send_window_end')}
                />
              </div>
              <div className="email-form-group">
                <label>Timezone</label>
                <select className="email-form-input" {...register('send_window_tz')}>
                  {TIMEZONES.map((tz) => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="email-timing-preview">{timingPreview}</div>
        </div>

        {/* Section 3: Dedupe Key */}
        <div className="email-form-section">
          <h4 className="email-section-title">Dedupe Key</h4>
          <div className="email-form-group">
            <input
              type="text"
              className="email-form-input"
              placeholder="e.g. {{reservation.id}}:confirmation:v1"
              {...register('dedupe_key_tpl')}
            />
            <span className="email-form-hint">
              Prevents duplicate sends. Use reservationId + email type + version.
            </span>
          </div>
        </div>

        {/* Section 4: Conditions */}
        <div className="email-form-section">
          <h4 className="email-section-title">Conditions</h4>
          <ConditionBuilder value={conditionExpr} onChange={setConditionExpr} />
        </div>

        <div className="email-form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSaving}
          >
            <Save size={16} />
            {isSaving ? 'Saving...' : 'Save Rule'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/admin/email/rules')}
          >
            <X size={16} />
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default EmailRuleEditor;
